import { IJsonQueryRangeValue } from '@cian/api-models/common/json_query';
import { Outside } from '@cian/ui-kit';
import { mergeStyles } from '@cian/utils';
import * as React from 'react';
import * as R from 'ramda';

import { getPriceLabel, getPriceTitle } from '../../utils/price';
import { NumberInput } from '../NumberInput';
import * as stylesCommon from '../FiltersCommon/FiltersCommon.css';
import * as styles from './FilterPrice.css';

export interface IFilterPriceProps {
  value: IJsonQueryRangeValue;
  setMin(value: number | undefined): void;
  setMax(value: number | undefined): void;
}

export interface IFilterPriceState {
  active: boolean;
  min: number | undefined;
  max: number | undefined;
  isErroneous: boolean;
}

export class FilterPrice extends React.Component<IFilterPriceProps, IFilterPriceState> {
  private validationTimeout: number;

  public constructor(props: IFilterPriceProps) {
    super(props);

    this.state = {
      active: false,
      min: props.value.gte,
      max: props.value.lte,
      isErroneous: false,
    };
  }

  public componentDidUpdate(prevProps: IFilterPriceProps) {
    if (
      !R.equals(prevProps, this.props) &&
      (this.props.value.gte !== this.state.min || this.props.value.lte !== this.state.max)
    ) {
      this.setState({
        min: this.props.value.gte,
        max: this.props.value.lte,
      });
    }

    if (!this.state.isErroneous) {
      if (this.validationTimeout) {
        window.clearTimeout(this.validationTimeout);
      }

      this.validationTimeout = window.setTimeout(this.validate, 500);
    }
  }

  public componentWillUnmount() {
    if (this.validationTimeout) {
      window.clearTimeout(this.validationTimeout);
    }
  }

  public render() {
    const { active, min, max, isErroneous } = this.state;

    const priceTitle = getPriceTitle(min, max) || undefined;
    const priceLabel = getPriceLabel(min, max);

    return (
      <Outside onOutside={this.handleOutsideClick}>
        <div {...mergeStyles(stylesCommon['container'], styles['container'])} data-mark="FilterPrice">
          <button
            {...mergeStyles(
              stylesCommon['button'],
              active && stylesCommon['button--active'],
              !priceLabel && stylesCommon['button--empty'],
            )}
            title={priceTitle}
            onClick={this.handleButtonClick}
          >
            {priceLabel || 'Цена'}
          </button>
          {active && (
            <div {...mergeStyles(stylesCommon['dropdown'], styles['dropdown'])}>
              <NumberInput
                className={styles['input']}
                placeholder="от"
                error={isErroneous}
                suffix={`\u20bd`}
                value={min}
                onValueChange={this.handleMinValueChange}
                onBlur={this.handleMinBlur}
              />
              <NumberInput
                className={styles['input']}
                placeholder="до"
                error={isErroneous}
                suffix={`\u20bd`}
                value={max}
                onValueChange={this.handleMaxValueChange}
                onBlur={this.handleMaxBlur}
              />
            </div>
          )}
        </div>
      </Outside>
    );
  }

  private handleButtonClick = () => {
    this.setState(state => ({
      active: !state.active,
    }));
  };

  private handleOutsideClick = () => {
    this.setState({
      active: false,
    });
  };

  private handleMinValueChange = (value: number | undefined) => {
    this.setState(state => ({
      min: value,
      isErroneous: state.isErroneous ? isValidInput(value, state.max) : false,
    }));
  };

  private handleMaxValueChange = (value: number | undefined) => {
    this.setState(state => ({
      max: value,
      isErroneous: state.isErroneous ? isValidInput(state.min, value) : false,
    }));
  };

  private handleMinBlur = () => {
    if (this.props.value.gte !== this.state.min) {
      this.props.setMin(this.state.min);
    }
  };

  private handleMaxBlur = () => {
    if (this.props.value.lte !== this.state.max) {
      this.props.setMax(this.state.max);
    }
  };

  private validate = () => {
    const { min, max } = this.state;
    this.setState({ isErroneous: isValidInput(min, max) });
  };
}

function isValidInput(min: number | undefined, max: number | undefined) {
  return !R.isNil(min) && !R.isNil(max) && min > max;
}
